import React, { useState, useEffect, useMemo } from 'react';
import { supervisoryAPI, lookupsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableCombobox } from '../components/SearchableCombobox';
import { Plus, Pencil, Trash2, Users, ShieldCheck, UserPlus, Search, Lock, ChevronRight } from 'lucide-react';

const emptyGroup = { name: '', notes: '', members: [], is_active: true };

export default function SupervisoryGroups() {
  const { user } = useAuth();
  // Supervisory groups define the chain of command — restricted to system
  // admins and managers only (2026-06-26 directive).
  const allowed = ['admin', 'manager'].includes(user?.role);
  const canEdit = allowed;
  const [groups, setGroups] = useState([]);
  const [levels, setLevels] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyGroup });
  const [pickEmp, setPickEmp] = useState('');
  const [pickLevel, setPickLevel] = useState('supervisor');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (allowed) loadAll(); else setLoading(false); }, [allowed]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [g, l, d] = await Promise.all([
        supervisoryAPI.listGroups(),
        supervisoryAPI.levels(),
        lookupsAPI.employees({ is_active: true }),
      ]);
      setGroups(g.data || []);
      setLevels(l.data || []);
      setDirectory(d.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load supervisory groups');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (id) => setExpanded(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const labelFor = (id) => levels.find(l => l.id === id)?.label || id;

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return groups;
    return groups.filter(g =>
      g.name.toLowerCase().includes(q) ||
      (g.members || []).some(m => (m.name || '').toLowerCase().includes(q)) ||
      (g.clients || []).some(c => (c.name || '').toLowerCase().includes(q))
    );
  }, [groups, search]);

  const openCreate = () => { setEditingId(null); setForm({ ...emptyGroup, members: [] }); setPickEmp(''); setOpen(true); };
  const openEdit = (g) => {
    setEditingId(g.id);
    setForm({
      name: g.name, notes: g.notes || '', is_active: g.is_active !== false,
      members: (g.members || []).map(m => ({ employee_id: m.employee_id, level: m.level, name: m.name })),
    });
    setPickEmp('');
    setOpen(true);
  };

  const addMember = () => {
    if (!pickEmp) { toast.error('Pick an employee first'); return; }
    const e = directory.find(x => x.id === pickEmp);
    setForm(f => ({
      ...f,
      members: [...f.members.filter(m => m.employee_id !== pickEmp),
                { employee_id: pickEmp, level: pickLevel, name: e ? `${e.first_name} ${e.last_name}` : pickEmp }],
    }));
    setPickEmp('');
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Group name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), notes: form.notes, is_active: form.is_active,
        members: form.members.map(m => ({ employee_id: m.employee_id, level: m.level })),
      };
      if (editingId) await supervisoryAPI.updateGroup(editingId, payload);
      else await supervisoryAPI.createGroup(payload);
      toast.success(editingId ? 'Group updated' : 'Group created');
      setOpen(false);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (g) => {
    try {
      await supervisoryAPI.deleteGroup(g.id);
      toast.success('Group deleted');
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const pickerItems = directory
    .filter(e => !form.members.some(m => m.employee_id === e.id))
    .map(e => ({
      _key: e.id,
      _searchStr: `${e.first_name} ${e.last_name} ${e.employee_number || ''} ${e.position || ''}`.toLowerCase(),
      ...e,
    }));

  return (
    <div className="space-y-6" data-testid="supervisory-groups-page">
      {!allowed ? (
        <div className="flex items-center justify-center h-96">
          <Card className="max-w-md">
            <CardContent className="p-6 text-center">
              <Lock className="w-8 h-8 mx-auto text-slate-400 mb-2" />
              <h2 className="text-lg font-semibold text-slate-700">Access denied</h2>
              <p className="text-sm text-slate-500 mt-1" data-testid="groups-access-denied">
                Supervisory groups are restricted to system administrators and managers.
              </p>
            </CardContent>
          </Card>
        </div>
      ) : (
      <>
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-slate-500" /> Supervisory Groups</h1>
          <p className="text-slate-500 mt-1">
            Build a chain of command once, attach it to any number of client accounts. Everyone in a group
            can see all employees assigned to those clients; the levels decide who writes and who approves.
          </p>
        </div>
        {canEdit && (
          <Button onClick={openCreate} data-testid="create-group-btn">
            <Plus className="w-4 h-4 mr-2" /> New Group
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10"
              placeholder="Search groups, people, clients..." data-testid="groups-search" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <p className="p-6 text-sm text-slate-400">Loading groups…</p>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center">
              <Users className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              <p className="text-slate-500">
                {groups.length === 0 ? 'No supervisory groups yet. Create one and attach it to your client accounts.' : 'No groups match your search.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Group</TableHead>
                  <TableHead>Chain of command</TableHead>
                  <TableHead>Attached clients</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right w-28">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(g => (
                  <TableRow key={g.id} data-testid={`group-row-${g.id}`}>
                    <TableCell className="font-medium align-top">
                      {g.name}
                      {g.notes && <p className="text-xs text-slate-500 mt-1 max-w-xs">{g.notes}</p>}
                    </TableCell>
                    <TableCell className="align-top">
                      {(g.members || []).length === 0 ? <span className="text-slate-400 text-sm">No members</span> : (
                        <div>
                          <button type="button" onClick={() => toggleExpanded(g.id)}
                            className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-slate-900 transition-colors"
                            data-testid={`group-chain-toggle-${g.id}`}>
                            <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${expanded.includes(g.id) ? 'rotate-90' : ''}`} />
                            {g.members.length} member{g.members.length === 1 ? '' : 's'}
                            <span className="text-slate-400 text-xs">
                              · {g.members.map(m => m.level_label || labelFor(m.level)).join(' → ')}
                            </span>
                          </button>
                          {expanded.includes(g.id) && (
                            <div className="mt-2 space-y-1 pl-5" data-testid={`group-chain-detail-${g.id}`}>
                              {g.members.map(m => (
                                <div key={m.employee_id} className="text-sm">
                                  <span className="font-medium">{m.name}</span>
                                  <span className="text-slate-500"> — {m.level_label || labelFor(m.level)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      {(g.clients || []).length === 0 ? <span className="text-slate-400 text-sm">Not attached</span> : (
                        <div className="flex flex-wrap gap-1 max-w-sm">
                          {g.clients.map(c => <Badge key={c.id} variant="secondary" className="font-normal">{c.name}</Badge>)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      {g.is_active === false ? <Badge variant="secondary">Inactive</Badge>
                        : <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Active</Badge>}
                    </TableCell>
                    <TableCell className="text-right align-top">
                      {canEdit && (
                        <>
                          <Button size="icon" variant="ghost" onClick={() => openEdit(g)} data-testid={`edit-group-${g.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => remove(g)} data-testid={`delete-group-${g.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="group-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit supervisory group' : 'New supervisory group'}</DialogTitle>
            <DialogDescription>
              Every level is optional. Whoever sits lowest in the group writes the evaluation; the next level
              up approves it. Assistant PM, PM and Regional Manager can always approve.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Group name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. OKC Metro Team" data-testid="group-name-input" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional — what this group covers" rows={2} data-testid="group-notes-input" />
            </div>

            <div className="flex flex-col md:flex-row items-stretch md:items-end gap-3">
              <div className="flex-1">
                <Label className="text-xs">Add person</Label>
                <SearchableCombobox
                  items={pickerItems}
                  value={pickEmp}
                  onSelect={(v) => setPickEmp(v || '')}
                  placeholder="Search employees…"
                  searchPlaceholder="Name or employee #"
                  emptyText="No employees found"
                  testId="group-member-picker"
                  renderItem={(e) => (e ? `${e.first_name} ${e.last_name}${e.position ? ` — ${e.position}` : ''}` : null)}
                />
              </div>
              <div className="w-full md:w-64">
                <Label className="text-xs">Level</Label>
                <Select value={pickLevel} onValueChange={setPickLevel}>
                  <SelectTrigger data-testid="group-level-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {levels.map(l => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={addMember} data-testid="group-add-member-btn">
                <UserPlus className="w-4 h-4 mr-1.5" /> Add
              </Button>
            </div>

            {form.members.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Name</TableHead><TableHead className="w-64">Level</TableHead><TableHead className="w-12" /></TableRow>
                </TableHeader>
                <TableBody>
                  {form.members
                    .slice()
                    .sort((a, b) => (levels.find(l => l.id === a.level)?.rank || 0) - (levels.find(l => l.id === b.level)?.rank || 0))
                    .map(m => (
                      <TableRow key={m.employee_id} data-testid={`group-member-${m.employee_id}`}>
                        <TableCell className="font-medium">{m.name}</TableCell>
                        <TableCell>
                          <Select value={m.level} onValueChange={(v) => setForm(f => ({
                            ...f, members: f.members.map(x => x.employee_id === m.employee_id ? { ...x, level: v } : x),
                          }))}>
                            <SelectTrigger data-testid={`group-member-level-${m.employee_id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {levels.map(l => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="icon" variant="ghost" data-testid={`group-member-remove-${m.employee_id}`}
                            onClick={() => setForm(f => ({ ...f, members: f.members.filter(x => x.employee_id !== m.employee_id) }))}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            )}

            <div className="flex items-center justify-between border-t pt-4">
              <Label>Active</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                data-testid="group-active-switch" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="group-save-btn">
              {saving ? 'Saving…' : (editingId ? 'Save changes' : 'Create group')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
}
